import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { saveArtistVotesMutationFn } from '../api/artistVotes';
import { queryKeys } from '../lib/query/queryKeys';
import { voteKey } from '../utils/lineupFormat';

const SAVE_DELAY_MS = 400;

/**
 * Picks you can tap as fast as you like (the Sets page's +/-, operator 2026-09-23).
 *
 * A save replaces your whole pick list for the event, so two saves racing would drop a pick. Here a tap updates the
 * screen at once; one save goes out 400 ms after the last tap with everything you have picked, and saves never
 * overlap - a later one waits for the earlier. The server copy takes over again once it has caught up.
 *
 * Returns { pickedKeys, isPicked(artistId, day), toggle(artistId, day), toggleKeys(keys) }. toggleKeys moves several
 * keys together (a b2b's artists): all off if any is on, else all on.
 */
export default function useQuickPicks({ eventId, userId, groupId = null, savedKeys }) {
  const queryClient = useQueryClient();
  const [desired, setDesired] = useState(null); // Set of keys while a change is unsaved, else null (use server's)
  const desiredRef = useRef(null);
  const timerRef = useRef(null);
  const chainRef = useRef(Promise.resolve());

  const pickedKeys = desired ?? savedKeys;

  // A new event (or user) starts from the server again.
  useEffect(() => {
    desiredRef.current = null;
    setDesired(null);
  }, [eventId, userId]);

  const flush = useCallback(() => {
    timerRef.current = null;
    const keys = desiredRef.current;
    if (!keys || !eventId || !userId) return;
    const selections = [...keys].map((k) => {
      const [artistId, day] = k.split('|');
      return { artistId, performanceDay: day || null };
    });
    chainRef.current = chainRef.current
      .catch(() => {})
      .then(() => saveArtistVotesMutationFn({ eventId, userId, selections }))
      .then(() =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: queryKeys.events.myArtistVotes(eventId, userId),
          }),
          groupId
            ? queryClient.invalidateQueries({
                queryKey: queryKeys.chats.memberVoters(groupId, eventId),
              })
            : null,
        ]),
      )
      .then(() => {
        // Only hand back to the server copy if nothing was tapped while this save was in flight.
        if (desiredRef.current === keys) {
          desiredRef.current = null;
          setDesired(null);
        }
      })
      .catch((err) => {
        console.error('[useQuickPicks] save failed:', err);
      });
  }, [eventId, userId, groupId, queryClient]);

  const toggleKeys = useCallback(
    (keys) => {
      if (!userId || !eventId || !keys?.length) return;
      const next = new Set(desiredRef.current ?? savedKeys);
      if (keys.some((k) => next.has(k))) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      desiredRef.current = next;
      setDesired(next);
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(flush, SAVE_DELAY_MS);
    },
    [userId, eventId, savedKeys, flush],
  );

  const toggle = useCallback(
    (artistId, performanceDay) => toggleKeys([voteKey(artistId, performanceDay)]),
    [toggleKeys],
  );

  // Leaving the page mid-debounce still saves.
  useEffect(
    () => () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        flush();
      }
    },
    [flush],
  );

  const isPicked = useCallback(
    (artistId, performanceDay) => pickedKeys.has(voteKey(artistId, performanceDay)),
    [pickedKeys],
  );

  return useMemo(
    () => ({ pickedKeys, isPicked, toggle, toggleKeys }),
    [pickedKeys, isPicked, toggle, toggleKeys],
  );
}
